/*
 * `v-reveal` — fade-and-rise an element the first time it scrolls into view.
 * Usage: `<section v-reveal>` or `<li v-reveal="index * 60">` (stagger in ms).
 * Styles live in app.css (`.reveal` / `.is-visible`); reduced motion shows
 * everything immediately. One shared IntersectionObserver for the page.
 */
import type { Directive } from 'vue';

const ROOT_MARGIN = '0px 0px -8% 0px';
let observer: IntersectionObserver | null = null;

function observe(el: Element) {
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer?.unobserve(entry.target);
      }
    },
    { rootMargin: ROOT_MARGIN, threshold: 0.08 },
  );
  observer.observe(el);
}

export const vReveal: Directive<HTMLElement, number | undefined> = {
  mounted(el, binding) {
    if (typeof IntersectionObserver === 'undefined') return;
    el.classList.add('reveal');
    if (binding.value) el.style.setProperty('--reveal-delay', `${binding.value}ms`);
    observe(el);
  },
  unmounted(el) {
    observer?.unobserve(el);
  },
};
